import { Component } from '@theme/component';
import { fetchConfig, resetShimmer } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #quantityTimers = new Map();
  #pendingMutations = new Map();
  #mutationQueue = Promise.resolve();
  #mutationInFlight = false;
  #reconcileTimer;
  #reconcileAbortController;
  #reconcileVersion = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    this.#quantityTimers.forEach((timer) => clearTimeout(timer));
    this.#quantityTimers.clear();
    if (this.#reconcileTimer) clearTimeout(this.#reconcileTimer);
    this.#reconcileAbortController?.abort();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line, cartKey } = event.detail;

    if (!line && !cartKey) return;

    if (quantity === 0) {
      return this.onLineItemRemove(cartKey || line);
    }

    const mutationId = cartKey || `line:${line}`;
    const existingTimer = this.#quantityTimers.get(mutationId);
    if (existingTimer) clearTimeout(existingTimer);

    const lineItemRow = this.#findCartRow(cartKey, line);
    this.#setOptimisticQuantity(lineItemRow, quantity);

    if (!lineItemRow) return;

    const timer = setTimeout(() => {
      this.#quantityTimers.delete(mutationId);
      this.updateQuantity({ line, cartKey, quantity, action: 'change' });
    }, 80);
    this.#quantityTimers.set(mutationId, timer);
  };

  /**
   * Handles the line item removal.
   * @param {string | number} cartKeyOrLine - The stable line key or a line index fallback.
   */
  onLineItemRemove(cartKeyOrLine) {
    const cartKey = typeof cartKeyOrLine === 'string' ? cartKeyOrLine : undefined;
    const line = typeof cartKeyOrLine === 'number' ? cartKeyOrLine : undefined;
    const mutationId = cartKey || `line:${line}`;
    const existingTimer = this.#quantityTimers.get(mutationId);
    if (existingTimer) clearTimeout(existingTimer);
    this.#quantityTimers.delete(mutationId);

    this.updateQuantity({
      cartKey,
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#findCartRow(cartKey, line);

    if (!cartItemRowToRemove) return;
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');
    cartItemRowToRemove.setAttribute('aria-busy', 'true');
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} [config.line] - The one-based line index fallback.
   * @param {string} [config.cartKey] - The stable Shopify cart line key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const mutationId = config.cartKey || `line:${config.line}`;
    this.#pendingMutations.set(mutationId, config);

    this.#mutationQueue = this.#mutationQueue
      .catch(() => undefined)
      .then(async () => {
        const latestConfig = this.#pendingMutations.get(mutationId);
        if (!latestConfig) return;

        this.#pendingMutations.delete(mutationId);
        await this.#performQuantityUpdate(latestConfig, mutationId);
      });

    return this.#mutationQueue;
  }

  async #performQuantityUpdate(config, mutationId) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const { line, cartKey, quantity } = config;
    const row = this.#findCartRow(cartKey, line);
    row?.classList.add('is-updating');
    row?.setAttribute('aria-busy', 'true');

    const body = JSON.stringify({
      ...(cartKey ? { id: cartKey } : { line }),
      quantity: quantity,
    });
    this.#mutationInFlight = true;

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const cartSnapshot = await response.json();

      resetShimmer(this);

      if (!response.ok || cartSnapshot.errors) {
        this.#handleCartError(config, cartSnapshot);
        return;
      }

      this.#applyCartSnapshot(cartSnapshot, config);

      this.dispatchEvent(
        new CartUpdateEvent(cartSnapshot, this.sectionId, {
          itemCount: Number(cartSnapshot.item_count) || 0,
          source: 'cart-items-component',
          cartSnapshot,
          skipSectionRender: true,
          preserveUpsell: true,
        })
      );

      this.#reapplyPendingUI();
      this.#scheduleSectionReconciliation({ delay: 220, preserveUpsell: true });
    } catch (error) {
      console.error(error);
      this.#handleCartError(config, { errors: 'We could not update your bag. Please try again.' });
    } finally {
      this.#mutationInFlight = false;
      const currentRow = this.#findCartRow(cartKey, line);
      if (!this.#pendingMutations.has(mutationId)) {
        currentRow?.classList.remove('is-updating', 'removing');
        currentRow?.removeAttribute('aria-busy');
      }
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {Object} config - The failed mutation.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (config, parsedResponseText) => {
    const row = this.#findCartRow(config.cartKey, config.line);
    const quantityInput = row?.querySelector('quantity-selector-component input');
    const selectedQuantity = row?.querySelector('.cart__quantity--selected');

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
      if (selectedQuantity) selectedQuantity.textContent = quantityInput.defaultValue;
    }

    row?.classList.remove('is-updating', 'removing');
    row?.removeAttribute('aria-busy');

    const cartItemError = row?.querySelector('.cart-item__error-text');
    const cartItemErrorContainer = row?.querySelector('.cart-items__error');
    const message = typeof parsedResponseText.errors === 'string'
      ? parsedResponseText.errors
      : 'We could not update your bag. Please try again.';

    if (cartItemError) cartItemError.textContent = message;
    cartItemErrorContainer?.classList.remove('hidden');
  };

  #findCartRow(cartKey, line) {
    const rows = Array.isArray(this.refs.cartItemRows) ? this.refs.cartItemRows : [];
    if (cartKey) return rows.find((candidate) => candidate.dataset.cartKey === cartKey);
    return line ? rows[line - 1] : undefined;
  }

  #setOptimisticQuantity(row, quantity) {
    if (!row) return;
    const input = row.querySelector('quantity-selector-component input');
    const selectedQuantity = row.querySelector('.cart__quantity--selected');
    if (input instanceof HTMLInputElement) input.value = String(quantity);
    if (selectedQuantity) selectedQuantity.textContent = String(quantity);
    row.classList.add('is-updating');
    row.setAttribute('aria-busy', 'true');
  }

  #reapplyPendingUI() {
    this.#pendingMutations.forEach((config) => {
      this.#setOptimisticQuantity(this.#findCartRow(config.cartKey, config.line), config.quantity);
    });
  }

  /**
   * Applies the lightweight JSON returned by Shopify immediately. The full
   * Liquid section is reconciled later so the quantity, prices and item count
   * never wait on expensive gift and recommendation rendering.
   * @param {Object} cartSnapshot - Shopify Ajax cart JSON.
   * @param {Object} [config] - The mutation that produced the snapshot.
   */
  #applyCartSnapshot(cartSnapshot, config) {
    if (!Array.isArray(cartSnapshot?.items)) return;

    const itemsByKey = new Map(cartSnapshot.items.map((item) => [String(item.key), item]));
    const rows = Array.isArray(this.refs.cartItemRows) ? [...this.refs.cartItemRows] : [];

    rows.forEach((row) => {
      const item = itemsByKey.get(String(row.dataset.cartKey || ''));
      if (item) {
        this.#updateRowFromCartItem(row, item, cartSnapshot.currency);
        return;
      }

      const matchesRemovedLine = config?.quantity === 0
        && (config.cartKey ? row.dataset.cartKey === config.cartKey : rows[Number(config.line) - 1] === row);
      if (matchesRemovedLine) row.remove();
    });

    const itemCount = Number(cartSnapshot.item_count) || 0;
    const hiddenCount = this.querySelector('[ref="cartItemCount"]');
    if (hiddenCount) hiddenCount.textContent = String(itemCount);

    const sectionCount = this.querySelector('.cart-items__section-count');
    if (sectionCount) sectionCount.textContent = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;

    const cartTotal = this.querySelector('[ref="cartTotal"]');
    if (cartTotal instanceof HTMLElement) {
      this.#setMoneyText(cartTotal, Number(cartSnapshot.total_price) || 0, cartSnapshot.currency);
      cartTotal.removeAttribute('shimmer');
    }

    const progressBar = this.querySelector('cart-progress-bar');
    if (progressBar instanceof HTMLElement) {
      progressBar.dataset.cartTotal = String((Number(cartSnapshot.total_price) || 0) / 100);
    }

    const upsell = this.querySelector('[data-native-cart-upsell]');
    if (upsell instanceof HTMLElement) upsell.dataset.cartTotal = String(Number(cartSnapshot.total_price) || 0);
  }

  /**
   * Updates an already-rendered cart row from a Shopify Ajax line item.
   * @param {HTMLElement} row
   * @param {Object} item
   * @param {string} [currency]
   */
  #updateRowFromCartItem(row, item, currency) {
    const quantity = Number(item.quantity) || 0;
    const quantityInput = row.querySelector('quantity-selector-component input');
    const selectedQuantity = row.querySelector('.cart__quantity--selected');

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = String(quantity);
      quantityInput.defaultValue = String(quantity);
      quantityInput.setAttribute('value', String(quantity));
    }
    if (selectedQuantity) selectedQuantity.textContent = String(quantity);

    const linePrice = row.querySelector('.cart-items__price text-component');
    if (linePrice instanceof HTMLElement) {
      const cents = Number(item.final_line_price ?? item.line_price) || 0;
      this.#setMoneyText(linePrice, cents, currency);
      linePrice.removeAttribute('shimmer');
    }

    row.classList.remove('is-updating', 'removing');
    row.removeAttribute('aria-busy');
  }

  /**
   * Makes repeat add-to-cart on an existing line feel immediate.
   * @param {Object} cartItem - Shopify add.js line item response.
   */
  #applyAddedCartItem(cartItem) {
    const item = Array.isArray(cartItem?.items) ? cartItem.items[0] : cartItem;
    if (!item) return false;

    const rows = Array.isArray(this.refs.cartItemRows) ? this.refs.cartItemRows : [];
    const row = rows.find((candidate) => candidate.dataset.cartKey === String(item.key))
      || rows.find((candidate) => candidate.dataset.variantId === String(item.variant_id)
        && candidate.dataset.linePropertyCount === '0'
        && !candidate.dataset.sellingPlanId);

    if (!row) return false;
    this.#updateRowFromCartItem(row, item, item.currency);
    return true;
  }

  /**
   * @param {HTMLElement} element
   * @param {number} cents
   * @param {string} [currency]
   */
  #setMoneyText(element, cents, currency) {
    const formatted = this.#formatMoney(cents, currency, element.dataset.moneyWithCurrency === 'true');
    element.textContent = formatted;
    element.setAttribute('value', formatted);
  }

  /**
   * @param {number} cents
   * @param {string} [currency]
   * @param {boolean} [withCurrency]
   */
  #formatMoney(cents, currency, withCurrency = false) {
    const activeCurrency = currency || window.Shopify?.currency?.active || 'INR';
    const hasMinorUnits = Math.abs(cents) % 100 !== 0;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: activeCurrency,
        currencyDisplay: withCurrency ? 'code' : 'narrowSymbol',
        minimumFractionDigits: hasMinorUnits ? 2 : 0,
        maximumFractionDigits: hasMinorUnits ? 2 : 0,
      }).format(cents / 100);
    } catch (_error) {
      return `${window.currencySymbol || ''}${(cents / 100).toFixed(hasMinorUnits ? 2 : 0)}`;
    }
  }

  /**
   * Defers the heavier Liquid refresh until cart mutations are quiet. A unique
   * URL and AbortController prevent an older response from overwriting a newer
   * quantity chosen by the customer.
   * @param {Object} [options]
   * @param {number} [options.delay]
   * @param {boolean} [options.preserveUpsell]
   */
  #scheduleSectionReconciliation({ delay = 180, preserveUpsell = false } = {}) {
    this.#reconcileVersion += 1;
    const version = this.#reconcileVersion;

    if (this.#reconcileTimer) clearTimeout(this.#reconcileTimer);
    this.#reconcileAbortController?.abort();

    this.#reconcileTimer = setTimeout(async () => {
      if (this.#mutationInFlight || this.#pendingMutations.size || this.#quantityTimers.size) {
        this.#scheduleSectionReconciliation({ delay: 120, preserveUpsell });
        return;
      }

      const abortController = new AbortController();
      this.#reconcileAbortController = abortController;
      const sectionUrl = new URL(window.location.href);
      sectionUrl.searchParams.set('section_id', this.sectionId.replace(/^shopify-section-/, ''));
      sectionUrl.searchParams.set('_cart_refresh', String(version));

      try {
        const response = await fetch(sectionUrl.toString(), {
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
          signal: abortController.signal,
        });
        if (!response.ok) throw new Error(`Cart refresh failed with ${response.status}`);

        const sectionHTML = await response.text();
        if (abortController.signal.aborted || version !== this.#reconcileVersion || !this.isConnected) return;
        this.#morphSelfFromSection(sectionHTML, { preserveUpsell });
      } catch (error) {
        if (error?.name !== 'AbortError') console.error(error);
      } finally {
        if (this.#reconcileAbortController === abortController) this.#reconcileAbortController = undefined;
      }
    }, delay);
  }

  /**
   * Morphs only the cart component, not the entire header/navigation section.
   * @param {string} sectionHTML
   * @param {Object} [options]
   * @param {boolean} [options.preserveUpsell]
   */
  #morphSelfFromSection(sectionHTML, { preserveUpsell = false } = {}) {
    const fragment = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const candidates = [...fragment.querySelectorAll('cart-items-component')];
    const newComponent = candidates.find((candidate) => candidate.dataset.sectionId === this.sectionId) || candidates[0];
    if (!newComponent) throw new Error(`Missing updated cart component for ${this.sectionId}`);

    if (preserveUpsell) {
      const currentUpsell = this.querySelector('[data-native-cart-upsell]');
      const nextUpsell = newComponent.querySelector('[data-native-cart-upsell]');
      if (currentUpsell && nextUpsell) nextUpsell.replaceWith(currentUpsell.cloneNode(true));
    }

    morph(this, newComponent);
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const data = event.detail.data || {};
    if (data.cartSnapshot) this.#applyCartSnapshot(data.cartSnapshot);
    if (data.cartItem) this.#applyAddedCartItem(data.cartItem);

    if (data.skipSectionRender) {
      this.#scheduleSectionReconciliation({
        delay: data.cartItem ? 40 : 220,
        preserveUpsell: data.preserveUpsell === true,
      });
      return;
    }

    const cartItemsHtml = data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#morphSelfFromSection(cartItemsHtml);
    } else {
      this.#scheduleSectionReconciliation({ delay: 40 });
    }
  };

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

class CartProgressBar extends Component {
  #cleanupStarted = false;

  connectedCallback() {
    super.connectedCallback();
    queueMicrotask(() => this.runProgressLogic());
  }

  static get observedAttributes() {
    return ["data-cart-total"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected) {
      this.runProgressLogic();
    }
  }

  async runProgressLogic() {
    if (this.#cleanupStarted) return;

    const cartTotal = Number(this.dataset.cartTotal);
    if (!Number.isFinite(cartTotal)) return;

    const lineKeys = new Set();
    for (const giftNumber of this.getGiftNumbers()) {
      const exists = this.getAttribute(`data-gift-${giftNumber}-exist`) === 'true';
      const lineKey = this.getAttribute(`data-gift-${giftNumber}-key`);
      const minimum = Number(this.getAttribute(`data-gift-${giftNumber}-min`));
      const maximumAttribute = this.getAttribute(`data-gift-${giftNumber}-max`);
      const maximum = maximumAttribute === null ? Number.NaN : Number(maximumAttribute);
      const isBelowTier = Number.isFinite(minimum) && cartTotal < minimum;
      const isAboveTier = Number.isFinite(maximum) && maximum > 0 && cartTotal >= maximum;

      if (exists && lineKey && (isBelowTier || isAboveTier)) lineKeys.add(lineKey);
    }

    if (!lineKeys.size) return;
    this.#cleanupStarted = true;
    try {
      await this.removeInvalidGifts([...lineKeys]);
    } finally {
      this.#cleanupStarted = false;
    }
  }

  getGiftNumbers() {
    const giftNumbers = new Set();
    for (const attr of this.attributes) {
      const match = attr.name.match(/^data-gift-(\d+)-min$/);
      if (match) giftNumbers.add(Number(match[1]));
    }
    return [...giftNumbers].sort((a, b) => a - b);
  }

  async removeInvalidGifts(lineKeys) {
    this.#disableCartItems();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const updates = Object.fromEntries(lineKeys.map((lineKey) => [lineKey, 0]));
    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }));
      const parsedResponseText = await response.json();
      if (!response.ok || parsedResponseText.errors) throw new Error('Free gift cleanup failed');

      const sectionHTML = parsedResponseText.sections?.[this.sectionId];
      if (!sectionHTML) throw new Error(`Missing updated cart section ${this.sectionId}`);

      const newSectionHTML = new DOMParser().parseFromString(
        sectionHTML,
        'text/html'
      );

      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}
if (!customElements.get('cart-progress-bar')) {
  customElements.define('cart-progress-bar', CartProgressBar);
}

class CartProductCard extends Component {
  selectVariant(event) {
    const option = event.target?.closest?.('[data-upsell-variant]');
    if (!(option instanceof HTMLElement)) return;

    const masterIdField = this.querySelector('[js-card-product-variant-id]');
    const selectedVariantLabel = this.querySelector('[js-selected-variant-label]');
    const cardProductPrice = this.querySelector('[js-card-product-price]');
    const cardProductVariantLabel = this.querySelector('[js-card-product-variant-label]');
    const variantLabel = option.dataset.sizeLabel || option.dataset.size || '';

    if (masterIdField instanceof HTMLInputElement) masterIdField.value = option.dataset.variantId || '';
    if (selectedVariantLabel) selectedVariantLabel.textContent = variantLabel;
    if (cardProductVariantLabel) cardProductVariantLabel.textContent = variantLabel;
    if (cardProductPrice) cardProductPrice.textContent = option.dataset.variantPrice || '';

    this.querySelectorAll('[data-upsell-variant]').forEach((candidate) => {
      const selected = candidate === option;
      candidate.classList.toggle('is-selected', selected);
      candidate.setAttribute('aria-selected', String(selected));
    });

    const addButton = this.querySelector('[js-upsell-atc-btn]');
    if (addButton instanceof HTMLButtonElement) {
      addButton.disabled = false;
      addButton.setAttribute('aria-disabled', 'false');
    }

    option.closest('details')?.removeAttribute('open');
  }

  handleVariantKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.selectVariant(event);
  }
}

if (!customElements.get('cart-product-card')) {
  customElements.define('cart-product-card', CartProductCard);
}
